import { UnaMapRequest } from '../../una-routes';
import {
    castStringToSearchField,
    MapRequestSearchField,
} from '@/app/shared/components/kl-search-maprequests/kl-search-maprequests';
import * as UnaModel from '../../shared/una-model';
import * as KlipApi from '@/api/klip-api.proxy';
import { escape } from 'html-escaper';
import {computed, defineComponent, onBeforeMount, onMounted, reactive, ref, useTemplateRef, watch} from 'vue';
import KlSearchMaprequests from '@/app/shared/components/kl-search-maprequests/kl-search-maprequests.vue';
import DateUtil from '@/app/shared/helpers/date-util';
import {useUnaStore} from '@/app/una/una-store';
import {IFilterData, IMapRequestMeta} from '@/app/una/maprequest-store-context';
import {isEmpty} from 'lodash-es';
import { saveAs } from 'file-saver';

export default defineComponent({
    name: 'KlUnaReplied',
    emits: ['update-query'],
    props: {},
    setup(props, { emit }) {

        const searchMaprequests = useTemplateRef<InstanceType<typeof KlSearchMaprequests>>('searchMaprequests');

        const unaStore = useUnaStore();
        const replied = computed(() => unaStore.replied);

        const selectedZone_AllOption = 'all';
        const searchUsed = ref<boolean>(false);

        const mapRequests = computed((): UnaModel.IMapRequestListItem[] => replied.value.mapRequests);
        const mapRequestsColumns = computed((): KlipApi.IColumn[] => replied.value.mapRequestsColumns);
        const mapRequestsFetching = computed((): boolean => replied.value.mapRequestsFetching);
        const mapRequestsMeta = computed((): IMapRequestMeta => replied.value.mapRequestsMeta);

        const mapRequestDefaultFilter = computed((): MapRequestSearchField => {
            return _apiInputToFilter(replied.value.filterData.defaultSearchFilter);
        });

        const maprequestCurrentFilter = computed((): MapRequestSearchField => {
            return _apiInputToFilter(replied.value.mapRequestsInput);
        })

        const selectedZone = computed({
            get (): string {
                return replied.value.mapRequestsInput?.utilityNetworkAuthorityZoneId || selectedZone_AllOption;
            },
            set (newZoneId: string){
                replied.value.updateFilter(_applySelectedZoneToInput(replied.value.mapRequestsInput, newZoneId));
            }
        })

        const filterData = computed((): IFilterData<KlipApi.IGetMapRequestsRepliedInput> => replied.value.filterData);
        const mapRequestsHasZones = computed(() => !isEmpty(replied.value.filterData.zones));

        const showDownloadLink = computed((): boolean => !isEmpty(mapRequests.value));
        const downloadFileUriLoading = ref<boolean>(false);

        const usingArchivedFilter = computed(() => {
            return replied.value.mapRequestsInput.isArchived;
        });

        const pagerClicked = (page: number) => {
            replied.value.updatePagination(page);
        };

        const columnClicked = (column: KlipApi.IColumn) => {
            replied.value.updateSorting(column);
        };

        function _apiInputToFilter(input: KlipApi.IGetMapRequestsRepliedInput): MapRequestSearchField {
            if (!input) {
                return null;
            }
            return {
                searchField: input.searchField ? KlipApi.SearchField[input.searchField] : null,
                organisationId: input.organisationId,
                searchOption: input.organisationId || KlipApi.SearchOption.All.toString(),
                keyword: input.query || '',
                date: {
                    from: DateUtil.parseToDate(input.startDate),
                    to: DateUtil.parseToDate(input.endDate),
                },
                archive: input.isArchived,
                location: input.zoneGeometry ? JSON.parse(input.zoneGeometry) : null,
            };
        }

        function _filterToApi(searchFields: MapRequestSearchField): KlipApi.IGetMapRequestsRepliedInput {
            console.assert(searchFields, 'Invalid argument: searchFields is required');
            return {
                query: searchFields.keyword,
                organisationId: searchFields.organisationId, //searchFields.searchOption === KlipApi.SearchOption.All.toString() ? null : searchFields.searchOption,
                searchOption: !searchFields.organisationId ? KlipApi.SearchOption.All : KlipApi.SearchOption.SubOrganisation, // castStringToSearchOption(searchFields.searchOption, KlipApi.SearchOption.SubOrganisation) as any,
                searchField: castStringToSearchField(searchFields.searchField),
                startDate: DateUtil.formatForApi(searchFields.date.from, 'ISO'),
                endDate: DateUtil.formatForApi(searchFields.date.to, 'ISO'),
                isArchived: searchFields.archive,
                zoneGeometry: searchFields.location ? JSON.stringify(searchFields.location) : null,
            }
        }

        const _parseCell = (row: IRow, column: IColumn) => {
            const mapRequest = mapRequests.value.find((request) => request.mapRequestId === row.mapRequestId && request.unaZoneId === row.unaZoneId);
            const cellValue = escape(row[column.key])
            let cellTemplate = `<span>${cellValue}</span>`;

            if (mapRequest) {
                cellTemplate = `<kl-router-link :to='${JSON.stringify(UnaMapRequest)}' :params='{id:"${mapRequest.mapRequestId}", zoneId:"${mapRequest.unaZoneId}", archived:"${mapRequest.isArchived}"}'>${cellValue}</kl-router-link>`;
            }
            return {
                template: cellTemplate,
            };
        }

        onBeforeMount(async () => {
            await useUnaStore().initReplied(_parseCell);
        });


        const search = (searchFields: MapRequestSearchField) => {
            searchUsed.value = true;

            replied.value.updateFilter(_applyFilterToInput(replied.value.mapRequestsInput, searchFields));
        }

        const _applyFilterToInput = (input: KlipApi.IGetMapRequestsRepliedInput, searchFields: MapRequestSearchField): KlipApi.IGetMapRequestsRepliedInput => {
            const newInput = _filterToApi(searchFields);
            return {
                ...input,
                ...newInput,
            };
        }

        const _applySelectedZoneToInput = (input: KlipApi.IGetMapRequestsRepliedInput, zoneId: string): KlipApi.IGetMapRequestsRepliedInput => {
            const utilityNetworkAuthorityZoneId = zoneId === selectedZone_AllOption ? null : zoneId;
            return {
                ...input,
                utilityNetworkAuthorityZoneId,
            };
        }

        const clearSearch = () => {
            searchMaprequests.value.reset();
            selectedZone.value = null;
        }

        const switchToArchived = () => {
            searchMaprequests.value.switchToArchived();
        }

        const download = async () => {
            downloadFileUriLoading.value = true;

            const response: KlipApi.FileResponse = await unaStore.downloadCurrentReplied();
            saveAs(response.data, response.fileName);

            downloadFileUriLoading.value = false;
        }

        return {
            mapRequests,
            searchUsed,
            mapRequestsMeta,
            mapRequestsColumns,
            mapRequestsFetching,
            selectedZone,

            pagerClicked,
            columnClicked,
            search,
            clearSearch,

            filterData,
            mapRequestsHasZones,
            mapRequestDefaultFilter,
            maprequestCurrentFilter,


            showDownloadLink,
            downloadFileUriLoading,
            download,

            usingArchivedFilter,
            switchToArchived,
        };
    }
})
